import { Inject, Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Router } from '@angular/router'
import { CollectionFloApiResponse, NodesFloApiResponse } from 'src/io/models/http/collections'
import { combineLatest, Observable, of, throwError } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { CollectionRequestParams } from 'src/io/models/http/collection-request-params'
import { defaultCollectionRequestParams } from '../default-collection-request-params'
import { defaultCollectionChildNodesRequestParams } from '../default-collection-child-nodes-request-params'
import { CollectionChildNodesRequestParams } from 'src/io/models/http/collection-child-nodes-request-params'
import { PaginatedRequestService } from '../../common/paginated-request.service'
import { FloApiResponse } from 'src/app/shared/models/api-response.model'
import { Node } from 'src/io/models/node'
import { EnvironmentService } from 'src/app/singleton-services/environment.service'
import { MultipleSectionsViewModel } from '@flocasts/experience-service-types'
import {
  EXPERIENCE_API_VERSION,
  ExperienceCollectionDetailSupportedTabs,
  EXPERIENCE_API_COLLECTION_DETAIL_BASE_BATH,
  EXPERIENCE_API_LEGACY_CORE_COLLECTIONS_TAG_PATH,
  EXPERIENCE_API_LEGACY_CORE_RECENT_COLLECTIONS_PATH
} from '../../../app.config'
import { LOGGER_SERVICE } from '../../../logger/logger.config'
import { LoggerService } from '../../../logger/logger.interface'
import { AthleteResults } from 'src/app/collections/collection.facade'
import { ExperimentationService } from '../../../shared/experimentation/experimentation.service'
import {
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION
} from '../../../shared/experimentation/experiment.model'

type WithIndex<A> = A & { [key: string]: string }
const STATX_ENTITY_RESULTS_FIELDS = ['winLossDraw', 'vs', 'replay', 'method', 'event', 'weight', 'date']

/*
 * Access to FloSports Core API endpoints `api/collections/*`
 */
@Injectable()
export class CollectionsService {
  constructor(
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly httpClient: HttpClient,
    private readonly paginatedRequestService: PaginatedRequestService,
    private readonly env: EnvironmentService,
    private readonly router: Router,
    private experimentationService: ExperimentationService
  ) {}

  public readonly isNextGenEventHub$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION,
    false
  )
  public readonly isNextGenEventHubShort$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
    false
  )

  public readonly getCollectionById = (
    collectionId: string,
    inputParams: CollectionRequestParams = defaultCollectionRequestParams
  ) =>
    !!inputParams
      ? this.httpClient
          .get<CollectionFloApiResponse>(`collections/${collectionId}`, {
            params: new HttpParams({
              fromObject: getCollectionsRequestParams(inputParams)
            })
          })
          .pipe(map(response => (response.data ? response : { ...response, data: {} })))
      : throwError(new Error('invalid input params provided'))

  public readonly getCollectionByTag = (
    collectionTag: string,
    inputParams: CollectionRequestParams = defaultCollectionRequestParams
  ) =>
    !!inputParams
      ? this.httpClient
          .get<CollectionFloApiResponse>(`${EXPERIENCE_API_LEGACY_CORE_COLLECTIONS_TAG_PATH}/${collectionTag}`, {
            params: new HttpParams({
              fromObject: {
                ...getCollectionsRequestParams(inputParams, true)
              }
            })
          })
          .pipe(
            tap(res => {
              /**
               * Redirect to the people list bc collection tags for athletes are deprecated.
               */
              const type = res.data?.original_entity?.type
              if (type === 'person') {
                this.router.navigateByUrl('/people')
              }
            }),
            map(response => (response.data ? response : { ...response, data: {} }))
          )
      : throwError(new Error('invalid input params provided'))

  public readonly getCollectionByNodeId = (
    nodeId: number,
    inputParams: CollectionRequestParams = defaultCollectionRequestParams
  ) =>
    !!inputParams
      ? this.httpClient
          .get<CollectionFloApiResponse>(`collections/from-node/${nodeId}`, {
            params: new HttpParams({
              fromObject: getCollectionsRequestParams(inputParams)
            })
          })
          .pipe(map(response => (response.data ? response : { ...response, data: {} })))
      : throwError(new Error('invalid input params provided'))

  public readonly getCollectionNodesById = (
    collectionId: string,
    inputParams: CollectionChildNodesRequestParams = defaultCollectionChildNodesRequestParams,
    initialResponse?: FloApiResponse<ReadonlyArray<Node>>
  ) =>
    this.paginatedRequestService.getNewPaginatedRequest(
      `collections/${collectionId}/nodes`,
      getCollectionsRequestParams(inputParams),
      initialResponse
    )

  public readonly getCollectionNodesByTag = (
    collectionTag: string,
    inputParams: CollectionChildNodesRequestParams = defaultCollectionChildNodesRequestParams,
    initialResponse?: FloApiResponse<ReadonlyArray<Node>>
  ) =>
    this.paginatedRequestService.getNewPaginatedRequest(
      `${EXPERIENCE_API_LEGACY_CORE_COLLECTIONS_TAG_PATH}/${collectionTag}/nodes`,
      getCollectionsRequestParams(inputParams, true),
      initialResponse
    )

  public readonly getCollectionNodesByNodeId = (
    nodeId: number,
    inputParams: CollectionChildNodesRequestParams = defaultCollectionChildNodesRequestParams,
    initialResponse?: FloApiResponse<ReadonlyArray<Node>>
  ) => {
    return this.paginatedRequestService.getNewPaginatedRequest(
      `collections/from-node/${nodeId}/nodes`,
      getCollectionsRequestParams(inputParams),
      initialResponse
    )
  }

  public readonly getRelatedCollections = (nodeId: string | number) =>
    this.httpClient.get<NodesFloApiResponse>(`nodes/${nodeId}/related-collections`).pipe(
      map(response => (response.data ? response : { ...response, data: [] })),
      catchError(err => {
        this.logger.error(err.message, err.error)
        return of(undefined)
      })
    )

  public readonly getRecentlyPublishedCollections = () =>
    this.httpClient
      .get<NodesFloApiResponse>(EXPERIENCE_API_LEGACY_CORE_RECENT_COLLECTIONS_PATH, {
        params: { version: EXPERIENCE_API_VERSION }
      })
      .pipe(
        map(response => (response.data ? response : { ...response, data: [] })),
        catchError(err => {
          this.logger.error(err.message, err.error)
          return of(undefined)
        })
      )

  /**
   * @deprecated
   * Still being used on current Athlete Profile pages for the W-L-D header.
   * Statx is still powering the W-L-D header on the Athlete Profile page.
   * Web app will no longer be reliant on statx once the new athlet profile pages are released.
   */
  public readonly getStatXResultsByCoreNodeId = (coreNodeId: number, sport: string) => {
    const statXEndpoint = `${this.env.config.endpoints.statx}/${sport}/entities/${coreNodeId}/results`
    return this.httpClient.get<AthleteResults>(statXEndpoint, {
      params: new HttpParams().set('fields', STATX_ENTITY_RESULTS_FIELDS.join(','))
    })
  }

  /**
   * Get Results Tab for Collection View, from Experience Service. Include search & filters params, if present
   */
  public readonly getExperienceCollectionDetailTab = (
    nodeId: number,
    tab: ExperienceCollectionDetailSupportedTabs,
    queryStringParams?: Record<string, string>
  ): Observable<MultipleSectionsViewModel | undefined> => {
    return combineLatest([this.isNextGenEventHub$, this.isNextGenEventHubShort$]).pipe(
      switchMap(([isNextGenEventHub, isNextGenEventHubShort]) => {
        const params = new HttpParams({
          fromObject: {
            version: EXPERIENCE_API_VERSION,
            ...queryStringParams,
            isNextGenEventHub: (isNextGenEventHub || isNextGenEventHubShort) ?? 'false'
          }
        })

        return this.httpClient
          .get<MultipleSectionsViewModel>(`${EXPERIENCE_API_COLLECTION_DETAIL_BASE_BATH}/${nodeId}/${tab.toString()}`, {
            params
          })
          .pipe(
            catchError((error: HttpErrorResponse) => {
              this.logger.error('Collections Service Error: ', error)
              return of(undefined)
            })
          )
      })
    )
  }
}

const getCollectionsRequestParams = (
  inputParams: CollectionRequestParams,
  isExperienceServiceRequest?: boolean
): WithIndex<CollectionRequestParams> => {
  return Object.keys({
    ...(defaultCollectionRequestParams as any),
    ...inputParams
  })
    .filter(key => key !== 'pages')
    .filter(key => key !== 'collectionId')
    .filter(key => key !== 'collectionTag')
    .filter(key => key !== 'nodeId')
    .reduce((outputParams, key) => {
      const param = (inputParams as WithIndex<CollectionRequestParams>)[key]
      if (param === null || param === undefined) {
        return outputParams
      } else {
        const params = {
          ...outputParams,
          [key]: (inputParams as WithIndex<CollectionRequestParams>)[key]
        }
        if (isExperienceServiceRequest) {
          params.version = EXPERIENCE_API_VERSION
        }
        // Limit the size of the request and prevent timeouts
        // Data depth 2 needed for Favorite button to show correctly
        params.fields = 'data<2>'
        return params
      }
    }, {} as WithIndex<CollectionRequestParams>)
}
